import NewsletterSignup from '../../components/public/NewsletterSignup';
import { Helmet } from "react-helmet-async";
import Footer from "../../components/public/Footer";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Send, ChevronUp } from 'lucide-react';
import PropertyCard from '../../components/property/PropertyCard';
import BookingModal from '../../components/public/BookingModal';

const richGold = 'var(--color-gold)';

const translations = {
  En: {
    heroKicker: "Janus Capital Strategy", heroLine1: "Where capital", heroLine2: "meets clarity.",
    heroSub: "Janus Capital Strategy supports international investors, entrepreneurs and families in structuring their wealth at the intersection of premium real estate, asset allocation and economic sovereignty.",
    ctaHero: "Initiate Strategic Dialogue", ctaHeroSecondary: "Book a Consultation", philo1: "Janus Capital Strategy is a wealth advisory and strategy firm designed for a new generation of international investors.", introMid: "In an environment where capital flows, jurisdictions, assets and risks grow increasingly complex, performance alone is no longer enough. What makes the difference is the quality of the structure — one that connects residence, assets, capital mobility, protection and long-term vision.", philo2: "Wealth is not assembled piecemeal. It is organized.", forWhomTitle: "For investors who think in structures", forWhomText: "Janus Capital Strategy speaks to profiles who are not simply looking for an opportunity, but for a lasting organization of their capital.", forWhomLabel: "Our clients notably include:", forWhomItems: ["international entrepreneurs", "wealth-focused investors", "mobile families", "capital holders seeking to arbitrate between jurisdictions, real estate, liquidity and diversification assets."], differenceTitle: "An advisory practice, not a distribution model", differenceText: "We do not start from a product to sell, but from a need to clarify. This difference changes everything. It allows us to move beyond isolated opportunities and into a logic of wealth architecture — one that is clearer, more robust, and more defensible over time.", 
    janusShortTitle: "A complete wealth perspective",
    janusShortText1: "We help our clients build a coherent wealth architecture around four complementary pillars:",
    janusShortItems: ["Wealth Architecture Advisory — A global reading of your wealth structure, translated into a concrete action plan: jurisdictions, vehicles, providers and timeline.", "Asset Management — Portfolio allocation and diversification, including real estate as a core asset class.", "Digital Assets — Advisory on Bitcoin and selected digital assets for professionals and individuals, including compliance and regulatory guidance.", "Sovereignty — Company registration, visa procurement, international bank account opening and entrepreneurial structuring."],
    janusShortCta: "Discover Our History",
    expertiseTitle: "Four Pillars, One Logic", expertiseSub: "Structure, protect, orient and execute.", 
    p1T: "Wealth Architecture Advisory", p1S: "Pillar 01", p1D: "Read your full wealth structure and turn strategy into concrete execution.", 
    p2T: "Asset Management", p2S: "Pillar 02", p2D: "Organize capital according to its real functions — return, security, liquidity, growth — with real estate as a core asset class.", 
    p3T: "Digital Assets", p3S: "Pillar 03", p3D: "Integrate Bitcoin and digital assets with discipline, advisory rigor and regulatory compliance.", 
    p4T: "Sovereignty", p4S: "Pillar 04", p4D: "Register companies, secure visas, open international bank accounts and structure your economic sovereignty.", 
    immoT: "REAL ESTATE", immoSub: "Exclusive opportunities in Dubai & Paris.", immoCta: "Discover full portfolio", 
    insightTitle: "FINANCIAL INSIGHTS", insightSub: "Macro analysis & Investment strategies", insightCta: "Access all insights", 
    contactTitle: "Building before optimizing", contactSub: "Modern wealth requires more than good intuition. It requires a precise reading of structures, jurisdictions, assets and life objectives. Janus Capital Strategy supports this thinking with a demanding, contemporary and long-term approach.", contactBtn: "Talk to an advisor",
    cercleTitle: "The Janus Circle", cercleSub: "Collective intelligence.", cercleText: "A private strategic council, accessible by invitation only. Share high-level analysis and shape your future.", cercleCta: "Join Circle"
  },
  Fr: {
    heroKicker: "Janus Capital Strategy", heroLine1: "Le capital,", heroLine2: "en toute clarté.",
    heroSub: "Janus Capital Strategy accompagne les investisseurs, entrepreneurs et familles internationales dans la structuration de leur patrimoine à l'intersection de l'immobilier premium, de l'allocation d'actifs et de la souveraineté économique.",
    ctaHero: "Découvrir notre approche", ctaHeroSecondary: "Prendre rendez-vous", philo1: "Janus Capital Strategy est un cabinet de réflexion et d'accompagnement patrimonial conçu pour une nouvelle génération d'investisseurs internationaux.", introMid: "Dans un environnement où les flux de capitaux, les juridictions, les actifs et les risques se complexifient, la performance seule ne suffit plus. Ce qui fait la différence est la qualité de la structure : celle qui relie résidence, actifs, mobilité du capital, protection et vision de long terme.", philo2: "Un patrimoine ne se juxtapose pas, il s'organise.", forWhomTitle: "Pour les investisseurs qui pensent en structure", forWhomText: "Janus Capital Strategy s'adresse aux profils qui ne cherchent pas simplement une opportunité, mais une organisation durable de leur capital.", forWhomLabel: "Nos clients sont notamment :", forWhomItems: ["des entrepreneurs internationaux", "des investisseurs patrimoniaux", "des familles mobiles", "des détenteurs de capital souhaitant arbitrer entre juridictions, immobilier, liquidité et actifs de diversification."], differenceTitle: "Une approche de cabinet, pas une logique de distribution", differenceText: "Nous ne partons pas d'un produit à vendre, mais d'un besoin à clarifier. Cette différence change tout. Elle permet de dépasser la logique d'opportunité isolée pour entrer dans une logique d'architecture patrimoniale : plus lisible, plus robuste et plus défendable dans le temps.", 
    janusShortTitle: "Une lecture patrimoniale complète",
    janusShortText1: "Nous aidons nos clients à construire une architecture patrimoniale cohérente autour de quatre axes complémentaires :",
    janusShortItems: ["Wealth Architecture Advisory — Une lecture globale de votre structure patrimoniale, traduite en plan d'action concret : juridictions, véhicules, prestataires et calendrier.", "Asset Management — Allocation et diversification de portefeuille, incluant l'immobilier comme classe d'actifs à part entière.", "Digital Assets — Conseil sur Bitcoin et actifs digitaux sélectionnés pour professionnels et particuliers, incluant compliance et accompagnement réglementaire.", "Sovereignty — Création de société, obtention de visa, ouverture de compte bancaire international et structuration entrepreneuriale."],
    janusShortCta: "Découvrir Notre Histoire",
    expertiseTitle: "Quatre Piliers, Une Seule Logique", expertiseSub: "Structurer, protéger, orienter et exécuter.", 
    p1T: "Wealth Architecture Advisory", p1S: "Pilier 01", p1D: "Lire l'ensemble de votre structure patrimoniale et transformer la stratégie en exécution concrète.", 
    p2T: "Asset Management", p2S: "Pilier 02", p2D: "Organiser le capital selon ses fonctions réelles — rendement, sécurité, liquidité, croissance — avec l'immobilier comme classe d'actifs à part entière.", 
    p3T: "Digital Assets", p3S: "Pilier 03", p3D: "Intégrer Bitcoin et les actifs digitaux avec discipline, rigueur de conseil et conformité réglementaire.", 
    p4T: "Sovereignty", p4S: "Pilier 04", p4D: "Créer des sociétés, sécuriser des visas, ouvrir des comptes bancaires internationaux et structurer votre souveraineté économique.", 
    immoT: "IMMOBILIER PRIVÉ", immoSub: "Opportunités exclusives à Dubaï & Paris.", immoCta: "Découvrir le portefeuille complet", 
    insightTitle: "ARTICLES & ANALYSES", insightSub: "Analyses macro & Stratégies d'investissement", insightCta: "Accéder à toutes les analyses", 
    contactTitle: "Construire avant d'optimiser", contactSub: "Le patrimoine moderne exige plus qu'une bonne intuition. Il exige une lecture précise des structures, des juridictions, des actifs et des objectifs de vie. Janus Capital Strategy accompagne cette réflexion avec une approche exigeante, contemporaine et orientée long terme.", contactBtn: "Parler à un conseiller",
    cercleTitle: "Le Cercle Janus", cercleSub: "Au-delà du conseil, l'intelligence collective.", cercleText: "Un conseil stratégique privé, accessible uniquement sur cooptation. Partagez des analyses de haut niveau et façonnez votre avenir.", cercleCta: "Rejoindre le Cercle Privé"
  },
};

const SEO_TITLE = 'Janus Capital Strategy · Wealth Architecture & Digital Assets';
const SEO_DESC = 'Wealth architecture for business owners, families and international investors: cross-border structuring, real estate and digital assets.';

const homeExtra = {
  En: {
    heroSub: "For business owners, families and international investors who want their wealth, digital assets included, organised in a structure that holds.",
    ctaStrategy: "Book a Strategy Session",
    ctaIntro: "Free introduction call",
    founderKicker: "THE FOUNDER",
    founderName: "Dimitri Yem, CGP · CFP®",
    founderBio: "In digital assets since 2014. Founded and sold a wealth advisory firm grown to more than €100M under advisory. In 2022, closed France’s first private real estate sale settled entirely in crypto, before a notary. Founder of NYX Card.",
    founderQuote: "I don’t start from a product to sell. I start from a situation to clarify.",
    founderCta: "Meet the founder →",
    stratKicker: "START HERE · STRATEGY SESSION · USD 499",
    stratH1a: "See the whole picture, ",
    stratH1b: "once.",
    stratLead: "Your wealth spans several countries and asset classes, digital assets included, and no one has ever looked at all of it with you.",
    stratB1: "A short questionnaire before the session",
    stratB2: "90 minutes, by video or in person",
    stratB3: "A written Priorities Memo within 5 business days: your objectives ranked, your constraints named, your three next steps",
    stratNote: "Payable on booking. Free rescheduling or full refund up to 48 hours before.",
    stratCta: "Book a Strategy Session",
    stratUnsure: "Not sure yet?",
    stratIntro: "Book a free 30-minute introduction call"
  },
  Fr: {
    heroSub: "Pour les dirigeants, les familles et les investisseurs internationaux qui veulent un patrimoine organisé dans une structure qui tient, actifs numériques compris.",
    ctaStrategy: "Réserver une Strategy Session",
    ctaIntro: "Appel découverte gratuit",
    founderKicker: "LE FONDATEUR",
    founderName: "Dimitri Yem, CGP · CFP®",
    founderBio: "Dans les actifs numériques depuis 2014. A fondé et cédé un cabinet de conseil patrimonial porté à plus de 100 M€ d’encours. En 2022, a conclu la première vente immobilière entre particuliers en France réglée intégralement en crypto, devant notaire. Fondateur de NYX Card.",
    founderQuote: "Je ne pars jamais d’un produit à vendre. Je pars d’une situation à clarifier.",
    founderCta: "Découvrir le fondateur →",
    stratKicker: "POUR COMMENCER · STRATEGY SESSION · 499 USD",
    stratH1a: "Voir l’ensemble, ",
    stratH1b: "une bonne fois.",
    stratLead: "Votre patrimoine traverse plusieurs pays et plusieurs classes d’actifs, actifs numériques compris, et personne ne l’a jamais regardé en entier avec vous.",
    stratB1: "Un court questionnaire avant la séance",
    stratB2: "90 minutes, en visio ou en personne",
    stratB3: "Un Priorities Memo écrit sous 5 jours ouvrés : vos objectifs classés, vos contraintes nommées, vos trois prochaines étapes",
    stratNote: "Réglé à la réservation. Report gratuit ou remboursement intégral jusqu’à 48 heures avant.",
    stratCta: "Réserver une Strategy Session",
    stratUnsure: "Pas encore sûr ?",
    stratIntro: "Réserver un appel découverte gratuit de 30 minutes"
  }
};

export default function Home() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem('januslang') || localStorage.getItem('janus_lang') || 'En');
  const [articles, setArticles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const t = translations[lang] || translations.En;
  const x = homeExtra[lang] || homeExtra.En;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resArt = await axios.get('/api/public/articles');
        if (resArt.data?.data) setArticles(resArt.data.data.slice(0, 3));
        const resProp = await axios.get('/api/public/real-estate');
        if (resProp.data?.data) setProperties(resProp.data.data.slice(0, 3));
      } catch (e) {}
    };
    fetchData();
    
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    const handleLangUpdate = () => {
      setLang((localStorage.getItem('januslang') || localStorage.getItem('janus_lang')) === 'Fr' ? 'Fr' : 'En');
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('januslangchanged', handleLangUpdate);
    window.addEventListener('langchange', handleLangUpdate); 
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('januslangchanged', handleLangUpdate);
      window.removeEventListener('langchange', handleLangUpdate);
    };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleOpenGlobalContact = (e) => {
    e?.preventDefault();
    window.dispatchEvent(new CustomEvent('openContactModal', { detail: { source: 'home' } }));
  };

  const goToStrategy = (e) => {
    e?.preventDefault();
    document.getElementById('strategy-session')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (window.location.hash === '#strategy-session') {
      const id = setTimeout(() => document.getElementById('strategy-session')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 700);
      return () => clearTimeout(id);
    }
  }, []);

  return (
    <div className={`janus-page font-light ${'text-left'}`} dir={'ltr'}>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESC} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESC} />
        <meta property="og:url" content="https://januscapitalstrategy.com/" />
        <meta property="og:image" content="https://januscapitalstrategy.com/uploads/images/1785081128428-Hero_V2.webp" />
        <meta property="og:site_name" content="Janus Capital" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESC} />
        <meta name="twitter:image" content="https://januscapitalstrategy.com/uploads/images/1785081128428-Hero_V2.webp" />
      </Helmet>

      {/* HERO */}
      <div className="relative h-screen w-full flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://januscapitalstrategy.com/uploads/images/1785081128428-Hero_V2.webp"
            alt="Janus Capital Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-10 pb-20 md:pb-28 text-left text-white">
          <div className="max-w-4xl">
            <h1 className="font-light tracking-tight mb-6 leading-tight">
              <span className="hero-kicker-in block text-[var(--color-gold)] font-black tracking-[0.3em] text-xs uppercase mb-6">{t.heroKicker}</span>
              <span className="hero-title-in block text-3xl md:text-6xl whitespace-nowrap">{t.heroLine1} {t.heroLine2}</span>
            </h1>
            <p className="hero-cta-in max-w-2xl text-base md:text-lg font-light text-white/85 leading-relaxed mb-12">{x.heroSub}</p>
            <div className="hero-cta-in flex flex-wrap gap-4">
              <button onClick={goToStrategy} className="inline-block bg-[var(--color-gold)] text-white px-12 py-5 text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white hover:text-[var(--color-black)] transition shadow-2xl">
                {x.ctaStrategy}
              </button>
              <button type="button" onClick={() => setBookingOpen(true)} className="inline-block border border-white/40 text-white px-12 py-5 text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white hover:text-[var(--color-black)] transition">{x.ctaIntro}</button>
            </div>
          </div>
        </div>
      </div>

      {/* PHILOSOPHY */}
      <div className="py-24 bg-transparent text-center border-b border-[var(--color-line)]">
        <div className="max-w-4xl mx-auto px-6 space-y-8">
            <p className="text-[var(--color-gold)] font-light text-2xl italic">"{t.philo1}"</p>
            <p className="janus-text-muted font-light leading-relaxed text-lg text-left md:text-center">{t.introMid}</p>
            <div className="w-12 h-[1px] bg-[var(--color-line)] mx-auto"></div>
            <h2 className="janus-title font-light text-3xl md:text-4xl leading-tight">{t.philo2}</h2>
        </div>
      </div>

      {/* JANUS SHORT */}
      <div id="janus-intro" className="py-32 bg-transparent">
        <div className="max-w-7xl mx-auto px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <h2 className="text-4xl font-light janus-title tracking-tight uppercase">{t.janusShortTitle}</h2>
            <div className="w-12 h-[1px] bg-[var(--color-gold)]"></div>
            <p className="janus-text-muted font-light leading-relaxed text-lg text-justify">{t.janusShortText1}</p>
            <ul className="space-y-3">
              {t.janusShortItems.map((item, i) => (
                <li key={i} className="flex gap-3 janus-text-muted font-light leading-relaxed text-lg text-justify">
                  <span className="text-[var(--color-gold)]">—</span><span>{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/janus" className="janus-cta mt-4 !px-10 !py-5 shadow-xl">
              {t.janusShortCta} <ArrowRight size={14} className="inline ml-2" />
            </Link>
          </div>
          <div className="w-full h-full min-h-[400px] relative rounded-lg overflow-hidden shadow-2xl">
            <img src="https://januscapitalstrategy.com/uploads/images/page_acueil_2.webp" alt="Janus Capital History" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/10"></div>
          </div>
        </div>

        {/* POUR QUI */}
        <div className="max-w-4xl mx-auto px-10 text-center mt-32">
          <h2 className="text-3xl font-light janus-title tracking-tight uppercase mb-6">{t.forWhomTitle}</h2>
          <p className="janus-text-muted font-light leading-relaxed text-lg mb-8">{t.forWhomText}</p>
          <p className="text-[var(--color-gold)] font-bold text-xs uppercase tracking-widest mb-6">{t.forWhomLabel}</p>
          <ul className="space-y-3 text-left max-w-xl mx-auto">
            {t.forWhomItems.map((item, i) => (
              <li key={i} className="flex gap-3 janus-text-muted font-light leading-relaxed text-lg">
                <span className="text-[var(--color-gold)]">—</span><span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* NOTRE DIFFERENCE */}
        <div className="max-w-3xl mx-auto px-10 text-center mt-32">
          <h2 className="text-3xl font-light janus-title tracking-tight uppercase mb-6">{t.differenceTitle}</h2>
          <p className="janus-text-muted font-light leading-relaxed text-lg">{t.differenceText}</p>
        </div>
      </div>

      {/* EXPERTISES */}
      <div id="expertise" className="py-32 bg-[var(--color-surface)]">
        <div className="max-w-7xl mx-auto px-10 text-center mb-24">
            <h2 className="text-5xl font-light mb-6 tracking-tight janus-title uppercase">{t.expertiseTitle}</h2>
            <p className="janus-text-muted font-bold text-sm tracking-widest uppercase">{t.expertiseSub}</p>
        </div>
        <div className="max-w-7xl mx-auto px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
            {[1, 2, 3, 4].map(num => (
              <div key={num} className="group border border-[var(--color-line)] p-8 hover:border-[var(--color-gold)] hover:bg-[var(--color-gold)] transition-all duration-500 bg-[var(--color-surface)] flex flex-col shadow-sm hover:shadow-xl rounded-sm">
                <div className="flex justify-between items-center mb-6">
                  <div className="text-[var(--color-line)] group-hover:text-white/20 font-black text-6xl transition-colors duration-500">0{num}</div>
                  <p className="text-[var(--color-gold)] group-hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors duration-500">{t[`p${num}S`]}</p>
                </div>
                <h3 className="text-xl font-bold mb-4 janus-title group-hover:text-white transition-colors duration-500 h-14 flex items-center">{t[`p${num}T`]}</h3>
                <p className="janus-text-muted group-hover:text-white/90 font-light leading-relaxed text-sm flex-grow">{t[`p${num}D`]}</p>
              </div>
            ))}
        </div>
        <div className="text-center mt-16">
          <button onClick={handleOpenGlobalContact} className="janus-cta !px-12 !py-5 shadow-xl !rounded-sm">
            {lang === 'Fr' ? 'Initier un projet' : 'Initiate a Project'}
          </button>
        </div>
      </div>

      {/* FOUNDER */}
      <div id="founder" className="py-28 md:py-32 bg-transparent border-t border-[var(--color-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 items-center">
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <div className="w-full max-w-sm aspect-[4/5] overflow-hidden rounded-t-full shadow-2xl">
              <img src="https://januscapitalstrategy.com/uploads/images/1791777671119-WOYTEK_KONARZEWSKI__DSC3521_N.webp" alt="Dimitri Yem" className="w-full h-full object-cover object-top" loading="lazy" />
            </div>
          </div>
          <div className="lg:col-span-7 text-left">
            <p className="text-[var(--color-gold)] font-bold text-xs uppercase tracking-[0.3em] mb-6">{x.founderKicker}</p>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight janus-title mb-8">{x.founderName}</h2>
            <p className="janus-text-muted font-light leading-loose text-lg mb-8 max-w-2xl">{x.founderBio}</p>
            <p className="text-xl md:text-2xl font-light italic leading-relaxed janus-title mb-10 max-w-2xl">{x.founderQuote}</p>
            <a href="/janus" className="inline-flex items-center border border-[var(--color-gold)] text-[var(--color-gold)] px-10 py-4 text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-[var(--color-gold)] hover:text-white transition">{x.founderCta}</a>
          </div>
        </div>
      </div>

      {/* STRATEGY SESSION */}
      <div id="strategy-session" className="scroll-mt-20 py-28 md:py-32" style={{ background: '#0A1228', color: '#F6F4EF' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div className="text-left">
            <div className="flex items-center gap-4 mb-8 text-[11px] md:text-xs font-medium uppercase tracking-[0.24em]" style={{ color: '#9DB4D8' }}>
              <span>{x.stratKicker}</span>
              <span className="hidden sm:inline-block w-14 h-px" style={{ background: '#C9A961' }}></span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8">
              {x.stratH1a}<em className="italic" style={{ color: '#9DB4D8' }}>{x.stratH1b}</em>
            </h2>
            <p className="text-lg md:text-xl font-light leading-relaxed max-w-xl" style={{ color: 'rgba(246,244,239,.82)' }}>{x.stratLead}</p>
          </div>
          <div className="p-8 md:p-10" style={{ border: '1px solid rgba(246,244,239,.16)', background: 'rgba(246,244,239,.03)', borderRadius: 3 }}>
            <ul className="space-y-5 mb-8">
              {[x.stratB1, x.stratB2, x.stratB3].map((item) => (
                <li key={item} className="flex gap-4 font-light leading-relaxed" style={{ color: 'rgba(246,244,239,.9)' }}>
                  <span className="mt-3 h-px w-5 shrink-0" style={{ background: '#C9A961' }}></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm font-light mb-8 pt-6" style={{ color: 'rgba(246,244,239,.62)', borderTop: '1px solid rgba(246,244,239,.16)' }}>{x.stratNote}</p>
            <div className="flex flex-col gap-5 items-start">
              <a href="https://januscapitalstrategy.com/telegram/#consulting" className="inline-block px-10 py-4 text-[10px] font-bold tracking-[0.2em] uppercase transition hover:opacity-90" style={{ background: '#F6F4EF', color: '#0A1228', borderRadius: 3 }}>{x.stratCta}</a>
              <p className="text-sm font-light" style={{ color: 'rgba(246,244,239,.62)' }}>
                {x.stratUnsure}{' '}
                <button type="button" onClick={() => setBookingOpen(true)} className="underline underline-offset-4 hover:opacity-80" style={{ color: '#9DB4D8' }}>{x.stratIntro}</button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ASSETS */}
      <div id="assets" className="py-32 bg-transparent border-t border-[var(--color-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={`flex flex-col ${'text-left'}`}>
              <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight janus-title">{t.immoT}</h2>
              <p className="text-[var(--color-gold)] font-bold text-xs uppercase tracking-[0.3em] mb-8">{t.immoSub}</p>
              <p className="janus-text-muted font-light leading-relaxed text-lg">
                {lang === 'Fr' ? "Découvrez des opportunités d'investissement exclusives, alliant rendements attractifs et sécurité patrimoniale. Notre expertise locale et notre réseau mondial nous permettent de vous offrir un accès privilégié aux biens les plus prestigieux." : "Discover exclusive investment opportunities, combining attractive yields and wealth security. Our local expertise and global network allow us to offer you privileged access to the most prestigious properties."}
              </p>
            </div>
            <div className="aspect-[16/9] w-full rounded-3xl overflow-hidden shadow-2xl relative border border-[var(--color-line)]">
              <img src="https://januscapitalstrategy.com/uploads/images/1783863163245-page_accueil_1.webp" alt="Real Estate" className="w-full h-full object-cover hover:scale-105 transition duration-700" />
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left mb-20">
            {properties.map(p => (
              <PropertyCard key={p._id} property={p} lang={lang} />
            ))}
          </div>
          <Link to="/portfolio" className="janus-cta !px-12 !py-5 shadow-xl">{t.immoCta}</Link>
        </div>
      </div>

      {/* INSIGHTS */}
      <div id="insights" className="py-32 bg-[var(--color-surface)] border-y border-[var(--color-line)]">
        <div className="max-w-7xl mx-auto px-10 text-center">
          <h2 className="text-3xl font-light janus-title">{t.insightTitle}</h2>
          <p className="janus-text-muted text-[10px] font-bold uppercase tracking-widest mb-20 mt-2">{t.insightSub}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-20 text-left">
             {articles.map(art => {
               const aTitle = art.titleEn || art.titleFr;
               return (
               <Link to={`/insights/${art.slug || art._id}`} key={art._id} className="janus-card p-8 group">
                 <div className="w-full h-40 janus-media-frame mb-6"><img src={art.image || 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=2232&auto=format&fit=crop'} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" /></div>
                 <p className="text-[var(--color-gold)] text-[10px] uppercase font-bold tracking-widest">{art.category || 'Strategy'}</p>
                 <h4 className="janus-title font-medium text-lg mt-3 line-clamp-2">{aTitle}</h4>
               </Link>
             )})}
          </div>
          <Link to="/insights" className="janus-cta-secondary !border-[var(--color-black)] hover:!bg-[var(--color-black)] hover:!text-white !px-12 !py-5">{t.insightCta}</Link>
        </div>
      </div>

      {/* CERCLE */}
      <div id="cercle" className="py-32 bg-[var(--color-black)]">
        <div className="max-w-5xl mx-auto px-10 text-center space-y-10">
          <Globe className="mx-auto mb-6" size={48} strokeWidth={1} style={{color: richGold}}/>
          <h2 className="text-4xl font-light text-white uppercase tracking-tight">{t.cercleTitle}</h2>
          <p className="text-[10px] font-bold uppercase tracking-[0.4em]" style={{color: richGold}}>{t.cercleSub}</p>
          <p className="text-gray-300 font-light leading-loose text-lg max-w-3xl mx-auto">{t.cercleText}</p>
          <div className="pt-8">
            <a href="https://januscapitalstrategy.com/telegram/" target="_blank" rel="noreferrer" className="inline-flex px-10 py-4 text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white hover:text-[var(--color-black)] transition items-center justify-center gap-3 border border-[var(--color-gold)] text-[var(--color-gold)]">
              <Send size={14}/> {t.cercleCta}
            </a>
          </div>
        </div>
      </div>

      {/* CONTACT */}
      <div id="contact" className="py-32 bg-[var(--color-surface)] border-t border-[var(--color-line)]">
        <div className="max-w-4xl mx-auto px-10 text-center">
          <div className="mb-10 pb-10 border-b border-[var(--color-line)]">
            <h2 className="text-4xl font-light janus-title uppercase">{t.contactTitle}</h2>
            <p className="janus-text-muted font-bold text-xs uppercase tracking-[0.3em] mt-2">{t.contactSub}</p>
          </div>
          <button 
            onClick={handleOpenGlobalContact} 
            className="janus-cta !px-12 !py-5 shadow-xl !rounded-sm uppercase tracking-widest text-xs"
          >
            {t.contactBtn}
          </button>
        </div>
      </div>

      {showScrollTop && (
        <button 
          onClick={scrollToTop} 
          className="fixed bottom-10 right-10 z-[110] bg-[var(--color-surface)] text-[var(--color-gold)] border-2 border-[var(--color-gold)] p-4 rounded-full shadow-2xl hover:bg-[var(--color-gold)] hover:text-white transition-all duration-300"
          aria-label="Scroll to Top"
        >
          <ChevronUp size={24} />
        </button>
      )}

      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} title={x.ctaIntro} />
      <NewsletterSignup lang={lang} sourcePage="home" />
      <Footer />
    </div>
  );
}
